import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getScanPaymentReturn, pollScanRequest } from "./scanPaymentReturn";
import { SCAN_LABELS } from "./scanRequestStatus";

const MESSAGES = {
    confirmed: "Paiement confirmé",
    pending: "Confirmation en attente. Vous pouvez actualiser le statut.",
    error: "Vérification impossible. Veuillez actualiser le statut.",
    authentication: "Reconnectez-vous pour vérifier votre demande.",
    review: "Vérification nécessaire",
    ready: "Signature confirmée. Préparation du paiement…",
    signature_stopped: "La signature nécessite votre attention. Consultez la demande.",
    payment_stopped: "Le backend indique que ce paiement n’a pas abouti. Consultez la demande.",
};

function ReturnVerification({ api, kind, id, onReady, onConfirmed, onUpdated, onReconnect }) {
    const { t } = useTranslation();
    const [attempt, setAttempt] = useState(0);
    const [result, setResult] = useState(null);
    const handlers = useRef({ onReady, onConfirmed, onUpdated });
    handlers.current = { onReady, onConfirmed, onUpdated };
    const current = result?.api === api && result?.attempt === attempt ? result : { status: "checking" };
    useEffect(() => {
        const controller = new AbortController();
        pollScanRequest({
            load: signal => kind === "payment" ? api.payment(id, signal) : api.get(id, signal),
            mode: kind, signal: controller.signal,
            onState: state => {
                setResult({ ...state, api, attempt });
                if (state.request) {
                    handlers.current.onUpdated?.(state.request);
                }
            },
        }).then(outcome => {
            if (controller.signal.aborted) return;

            if (outcome?.status === "ready") {
                handlers.current.onReady?.(outcome.request);
            }

            if (outcome?.status === "confirmed") {
                handlers.current.onConfirmed?.(outcome.request);
            }
        });
        return () => controller.abort();
    }, [api, kind, id, attempt]);
    const busy = ["checking", "waiting", "ready"].includes(current.status);
    return (
        <section className={`scan-payment-return scan-payment-return--${current.status}`}>
            <div role="status" aria-live="polite">
                <h2>{busy && <span className="scan-loading-bar" aria-hidden="true" />}
                    {t(["checking", "waiting"].includes(current.status)
                        ? kind === "payment" ? "Vérification du paiement…" : "Vérification de la signature…"
                        : MESSAGES[current.status])}
                </h2>
                {current.request?.scan_status && <p>{t(SCAN_LABELS[current.request.scan_status] || current.request.scan_status)}</p>}
            </div>
            {!busy && current.status !== "confirmed" && <button type="button" className="buttons-ecp buttons-ecp-create" onClick={() => setAttempt(value => value + 1)}>{t("Actualiser le statut")}</button>}
            {current.status === "authentication" && <button type="button" className="buttons-ecp buttons-ecp-create" onClick={onReconnect}>{t("Se reconnecter")}</button>}
        </section>
    );
}

export default function ScanCheckout({ api, onPay, onPaymentConfirmed, onUpdated, onReload, onResume, onReconnect }) {
    const { t } = useTranslation();
    const [params, setParams] = useSearchParams();
    const value = getScanPaymentReturn(params);
    const consumed = useRef(new Set());
    const callbacks = useRef({ onReload });
    callbacks.current = { onReload };
    useEffect(() => {
        if (value.kind === "interrupted") callbacks.current.onReload();
    }, [value.kind]);
    const clearReturn = () => {
        const next = new URLSearchParams(params);
        for (const name of ["paiement", "session_id", "signature", "request_id"]) next.delete(name);
        setParams(next, { replace: true });
    };
    if (value.kind === "none") return null;
    if (["payment", "signature"].includes(value.kind)) {
        return <ReturnVerification key={`${value.kind}:${value.id}`} api={api} kind={value.kind} id={value.id}
            onUpdated={onUpdated} onReconnect={onReconnect} onReady={request => {
                if (value.kind !== "signature") return;
                if (consumed.current.has(value.id)) return;

                consumed.current.add(value.id);

                clearReturn();
                onPay(request);
            }} onConfirmed={request => {
                if (value.kind !== "payment") return;
                if (consumed.current.has(value.id)) return;

                consumed.current.add(value.id);

                clearReturn();
                onPaymentConfirmed?.(request);
            }}
        />;
    }
    return (
        <section className="scan-payment-return">
            <div role="status">
                <h2>{t(value.kind === "interrupted" ? "Paiement interrompu" : "Référence de retour absente ou invalide")}</h2>
                <p>{t(value.kind === "interrupted"
                    ? "Ce retour ne confirme pas un échec définitif. Consultez votre demande pour reprendre le paiement."
                    : "Consultez l’historique ou rouvrez le lien de retour complet. Aucune demande ne sera sélectionnée automatiquement.")}</p>
            </div>
            <button type="button" className="buttons-ecp buttons-ecp-create" onClick={() => { clearReturn(); onResume(); }}>{t(value.kind === "interrupted" ? "Reprendre le parcours" : "Voir les demandes")}</button>
        </section>
    );
}
